// Pure status-derivation helpers for milestones.
//
// Two derivation families coexist:
//   1. Legacy pill / urgency helpers (derivePillStatus, deriveUrgencyStatus) —
//      keyed off the three-value stored status + actualDate model.
//   2. Lifecycle helpers (deriveMilestoneLifecycleStatus, deriveLate,
//      deriveAwaitingFullAcceptance) — keyed off the four persisted lifecycle
//      timestamps (deliveredAt/partiallyAcceptedAt/acceptedAt/paidAt). These
//      mirror the backend deriveMilestoneStatus.ts exactly; any change to
//      precedence order must be applied to both files simultaneously. Lock-step
//      parity for deriveMilestoneLifecycleStatus and deriveLate is covered by
//      the shared fixtures in `tests/shared/milestoneLifecycleFixtures/` (see
//      that directory's README), consumed by
//      `deriveMilestoneStatus.parity.test.ts` on both stacks.

import type {
  MilestoneStoredStatus,
  MilestoneDisplayStatus,
} from "../types";
import { getBrisbaneToday } from "./getBrisbaneToday";
import { isSet } from "./milestoneInput";
import { isoDayUTC, daysBetween } from "./isoDay";

const DUE_SOON_DAYS = 14;

/**
 * Effective completion check used across all milestone surfaces. A
 * milestone is treated as completed when EITHER stored=`completed` OR
 * an `actualDate` has been recorded — keeps every surface (pill,
 * schedule cell, urgency badge, summary aggregation, tick affordance)
 * in agreement against the same record. Without this, the pill could
 * say "Due soon" while the schedule cell rendered "✓ early" against
 * the same actualDate, because the pill keyed off stored only.
 */
export function isMilestoneCompleted(m: {
  status: MilestoneStoredStatus;
  actualDate?: string | null;
}): boolean {
  return m.status === "completed" || !!m.actualDate;
}

/**
 * Derive the pill (status-column badge) display status. Honours user
 * intent first: a `partially-completed` milestone stays partially-
 * completed even when its planned date has passed. A milestone with
 * `actualDate` set surfaces as completed regardless of stored status,
 * matching the schedule cell + summary-strip behaviour.
 */
export function derivePillStatus(
  stored: MilestoneStoredStatus,
  plannedDate: string,
  actualDate: string | undefined,
  today: string = getBrisbaneToday(),
): MilestoneDisplayStatus {
  if (isMilestoneCompleted({ status: stored, actualDate })) {
    if (actualDate && isoDayUTC(actualDate) > isoDayUTC(plannedDate)) {
      return "completed-late";
    }
    return "completed";
  }
  if (stored === "partially-completed") return "partially-completed";
  // stored === 'upcoming'
  if (isoDayUTC(plannedDate) < isoDayUTC(today)) return "overdue";
  if (daysBetween(today, plannedDate) <= DUE_SOON_DAYS) return "due-soon";
  return "upcoming";
}

/**
 * Derive the urgency display status — used by surfaces that should
 * surface slippage even when the user marked partial completion
 * (schedule cell, timeline node, number badge). For
 * `partially-completed` + overdue inputs this returns `overdue` while
 * `derivePillStatus` returns `partially-completed`. The disagreement is
 * intentional and tested.
 */
export function deriveUrgencyStatus(
  stored: MilestoneStoredStatus,
  plannedDate: string,
  actualDate: string | undefined,
  today: string = getBrisbaneToday(),
): MilestoneDisplayStatus {
  if (isMilestoneCompleted({ status: stored, actualDate })) {
    if (actualDate && isoDayUTC(actualDate) > isoDayUTC(plannedDate)) {
      return "completed-late";
    }
    return "completed";
  }
  // Slippage takes priority over partial-completion intent.
  if (isoDayUTC(plannedDate) < isoDayUTC(today)) return "overdue";
  if (stored === "partially-completed") return "partially-completed";
  if (daysBetween(today, plannedDate) <= DUE_SOON_DAYS) return "due-soon";
  return "upcoming";
}

// ─── Lifecycle helpers (four-timestamp model) ─────────────────────────

export type MilestoneDerivedStatus =
  | "upcoming"
  | "delivered"
  | "partially-accepted"
  | "accepted"
  | "paid";

export interface MilestoneLifecycleTimestamps {
  deliveredAt?: string | null;
  partiallyAcceptedAt?: string | null;
  acceptedAt?: string | null;
  paidAt?: string | null;
}


/**
 * Derive the milestone's lifecycle status from its four persisted timestamps.
 * Mirrors backend deriveMilestoneStatus.ts exactly — precedence order:
 * paid > accepted > partially-accepted > delivered > upcoming.
 * `paid` may sit on top of either `accepted` OR `partiallyAcceptedAt`
 * (the partial-payment carve-out).
 */
export function deriveMilestoneLifecycleStatus(
  m: MilestoneLifecycleTimestamps,
): MilestoneDerivedStatus {
  if (isSet(m.paidAt)) return "paid";
  if (isSet(m.acceptedAt)) return "accepted";
  if (isSet(m.partiallyAcceptedAt)) return "partially-accepted";
  if (isSet(m.deliveredAt)) return "delivered";
  return "upcoming";
}

/**
 * A milestone is late iff it was delivered after its planned date.
 * ISO string comparison is safe for YYYY-MM-DD because lexicographic
 * order matches chronological order. Mirrors backend deriveLate.
 */
export function deriveLate(
  m: { plannedDate?: string | null } & MilestoneLifecycleTimestamps,
): boolean {
  if (!isSet(m.deliveredAt)) return false;
  if (!isSet(m.plannedDate)) return false;
  return m.deliveredAt > m.plannedDate;
}

/**
 * A milestone is overdue iff it has not yet been delivered AND its
 * planned date is in the past. Distinct from `deriveLate`, which fires
 * once the (late) delivery has been recorded — overdue is the
 * pre-delivery slippage signal. Both surface as urgent visuals on the
 * timeline + stepper but with different tones (red for overdue, amber
 * for delivered-late) so users can distinguish action-needed from
 * historical-late.
 *
 * `today` defaults to Brisbane today; tests inject a stable value.
 */
export function deriveOverdue(
  m: { plannedDate?: string | null } & MilestoneLifecycleTimestamps,
  today?: string,
): boolean {
  if (isSet(m.deliveredAt)) return false;
  if (!isSet(m.plannedDate)) return false;
  const todayStr = today ?? getBrisbaneToday();
  return m.plannedDate < todayStr;
}

/**
 * Returns true when a milestone has been partially accepted but not yet
 * fully accepted — i.e. the agency still needs to issue full acceptance.
 * Narrow semantic — drives the amber callout above the partial-acceptance
 * row in the milestone expand panels. Orthogonal to paidAt: a paid-against-
 * partial milestone is still awaiting full acceptance.
 *
 * For filter-chip semantics (any delivered-but-not-yet-fully-accepted
 * milestone) use the broader `deriveAwaitingAcceptance` below.
 */
export function deriveAwaitingFullAcceptance(
  m: MilestoneLifecycleTimestamps,
): boolean {
  return isSet(m.partiallyAcceptedAt) && !isSet(m.acceptedAt);
}

/**
 * Returns true when a milestone has been delivered but is not yet fully
 * accepted, regardless of whether partial acceptance has been recorded.
 * Covers two lifecycle states for the "Awaiting acceptance" filter chip:
 *   1. Delivered, no acceptance recorded (deliveredAt set, acceptedAt unset)
 *   2. Delivered, partially accepted (deliveredAt set, partiallyAcceptedAt
 *      set, acceptedAt unset) — also satisfies the narrow predicate above.
 * Excludes future-dated milestones (no delivery yet) and fully accepted
 * milestones (the agency has signed off).
 */
export function deriveAwaitingAcceptance(
  m: MilestoneLifecycleTimestamps,
): boolean {
  return isSet(m.deliveredAt) && !isSet(m.acceptedAt);
}
